// Zod contracts for the Linear hand-off (C20, poc CT-009, module-14). Every request and response schema of the
// Linear routes lives here, with the Linear parts other contracts embed (the app's target, a ticket's issue).
//
//   GET  /api/linear/status                     staff                   -> LinearStatus
//   GET  /api/linear/teams                      app.register            -> { items: LinearTeam[] }
//   GET  /api/linear/teams/:teamId/projects     app.register            -> { items: LinearProject[] }
//   PUT  /api/apps/:appId/linear                app.manage_settings     LinearTargetInput -> AppView (contracts/apps)
//   POST /api/admin/linear/sync                 tool.manage_settings    -> LinearSyncResult
//   POST /api/tickets/:ref/linear/retry         ticket.assign           -> LinearRetryResult
// Registration (POST /api/apps) takes `linear: LinearTargetInput`, required while Linear is connected.
import { z } from 'zod';
import { LINEAR_HANDOFF_STATES } from '../constants/linear.js';
import { envelope } from './common.js';

const IsoDateTime = z.iso.datetime({ offset: true });
const LINEAR_ID_MAX = 100;
/** Linear's IDs are UUIDs; any short non-empty string is accepted, so a local fake Linear can use its own. */
export const LinearIdSchema = z.string().trim().min(1).max(LINEAR_ID_MAX);

// ---- Connection ------------------------------------------------------------------------------------------

export const LinearStatusSchema = z.object({
  /** Both client credentials are configured (env or Key Vault). Not connected: nothing is handed off. */
  connected: z.boolean(),
  /** The host the tool calls, for example api.linear.app (a local fake Linear in local runs). */
  apiHost: z.string(),
});
export type LinearStatus = z.infer<typeof LinearStatusSchema>;
export const LinearStatusResponseSchema = envelope(LinearStatusSchema);

// ---- Pickers (registration's Linear step, App settings) ---------------------------------------------------

/** A public, non-archived team the tool's app token can see. */
export const LinearTeamSchema = z.object({ id: z.string(), name: z.string(), key: z.string() });
export type LinearTeam = z.infer<typeof LinearTeamSchema>;
export const LinearTeamsResponseSchema = envelope(z.object({ items: z.array(LinearTeamSchema) }));

export const LinearProjectSchema = z.object({ id: z.string(), name: z.string() });
export type LinearProject = z.infer<typeof LinearProjectSchema>;
export const LinearProjectsResponseSchema = envelope(z.object({ items: z.array(LinearProjectSchema) }));

export const LinearTeamParamsSchema = z.object({ teamId: LinearIdSchema });

// ---- An app's Linear target ---------------------------------------------------------------------------------

/** Registration's `linear` and the body of PUT /api/apps/:appId/linear. The project must belong to the team. */
export const LinearTargetInputSchema = z.object({ teamId: LinearIdSchema, projectId: LinearIdSchema });
export type LinearTargetInput = z.infer<typeof LinearTargetInputSchema>;

/** The stored target, with the names and key read from Linear when it was chosen. */
export const AppLinearTargetSchema = z.object({
  teamId: z.string(),
  teamName: z.string(),
  /** The team's issue prefix, for example FBS. */
  teamKey: z.string(),
  projectId: z.string(),
  projectName: z.string(),
});
export type AppLinearTarget = z.infer<typeof AppLinearTargetSchema>;

// ---- A ticket's Linear issue (ticket detail, queue and dashboard rows, MCP) ----------------------------------

/**
 * Null on a ticket that was never handed off. Set once a hand-off is queued: `pending` with no issue yet,
 * `done` with the issue, `failed` with the reason in `lastError`. A `done` ticket's `lastError` is the last sync
 * problem, for example a Linear status the ticket's workflow can't mirror.
 */
export const TicketLinearSchema = z.object({
  handoff: z.enum(LINEAR_HANDOFF_STATES),
  /** FBS-12. Null until the issue exists. */
  identifier: z.string().nullable(),
  url: z.string().nullable(),
  /** Linear's own status name, for example "In Review". */
  stateName: z.string().nullable(),
  /** Linear's state type (constants/linear LINEAR_STATE_TYPES); kept as a string so a new Linear type still reads. */
  stateType: z.string().nullable(),
  /** Who has the issue in Linear (may be someone who isn't an Editor). Staff surfaces only. */
  assigneeName: z.string().nullable(),
  assigneeEmail: z.string().nullable(),
  lastError: z.string().nullable(),
});
export type TicketLinear = z.infer<typeof TicketLinearSchema>;

// ---- Admin and ticket actions ------------------------------------------------------------------------------

/** One sync pass (POST /api/admin/linear/sync, and the scheduler rule's summary). Counts only. */
export const LinearSyncResultSchema = z.object({
  /** Handed-off tickets that aren't finished, whose issues were read. */
  checked: z.number().int().min(0),
  /** Tickets changed through Ticket Core (`apply_linear_state`). */
  changed: z.number().int().min(0),
  /** Issues that couldn't be read or mirrored (each one's reason is on its ticket). */
  failed: z.number().int().min(0),
  /** When the pass finished. */
  syncedAt: IsoDateTime,
});
export type LinearSyncResult = z.infer<typeof LinearSyncResultSchema>;
export const LinearSyncResponseSchema = envelope(LinearSyncResultSchema);

/** "Retry hand-off": the ticket's failed hand-off is queued again (false: there was nothing to retry). */
export const LinearRetryResultSchema = z.object({ requeued: z.boolean() });
export type LinearRetryResult = z.infer<typeof LinearRetryResultSchema>;
export const LinearRetryResponseSchema = envelope(LinearRetryResultSchema);
